import React, { useCallback, useEffect, useLayoutEffect, useState, } from 'react';
import { createPortal } from 'react-dom';
import HLSAdapter from 'playable/dist/src/adapters/hls';
import { create, ENGINE_STATES, registerPlaybackAdapter, registerModule, VIDEO_EVENTS, UI_EVENTS, } from 'playable';
import s from './Playable.scss';
registerPlaybackAdapter(HLSAdapter);
const useFullScreenState = (player, onFullScreenChange) => {
    const handleFullscreenChanged = useCallback((isFullScreen) => {
        if (onFullScreenChange) {
            onFullScreenChange(isFullScreen);
        }
    }, [onFullScreenChange]);
    useEffect(() => {
        player.on(UI_EVENTS.FULL_SCREEN_STATE_CHANGED, handleFullscreenChanged);
        return () => {
            player.off(UI_EVENTS.FULL_SCREEN_STATE_CHANGED, handleFullscreenChanged);
        };
    }, [handleFullscreenChanged, player]);
};
const Player = ({ player, src, title, playing, onEnd, onPlay, onPause, onPlayableInit, muted, controlsHidden = false, loop = false, onFullScreenChange, coverUrl, }) => {
    useFullScreenState(player, onFullScreenChange);
    const handlePlay = useCallback(() => {
        onPlay && onPlay();
    }, [onPlay]);
    const handleEnd = useCallback(() => {
        onEnd && onEnd();
    }, [onEnd]);
    const handlePause = useCallback(() => {
        onPause && onPause();
    }, [onPause]);
    const handleStateChanged = useCallback(({ nextState }) => {
        switch (nextState) {
            case ENGINE_STATES.PLAYING:
                handlePlay();
                break;
            case ENGINE_STATES.PAUSED:
                handlePause();
                break;
            case ENGINE_STATES.ENDED:
                handleEnd();
                break;
        }
    }, [handlePlay, handleEnd, handlePause]);
    useEffect(() => {
        player.on(VIDEO_EVENTS.STATE_CHANGED, handleStateChanged);
        onPlayableInit && onPlayableInit(player);
        return () => {
            player.off(VIDEO_EVENTS.STATE_CHANGED, handleStateChanged);
        };
    }, [player, handleStateChanged, onPlayableInit]);
    useLayoutEffect(() => player.setSrc(src), [player, src]);
    useLayoutEffect(() => {
        if (controlsHidden) {
            player.hideMainUI();
            player.hideOverlay();
        }
        else {
            player.showMainUI();
            player.showOverlay();
        }
    }, [player, controlsHidden]);
    useLayoutEffect(() => (muted ? player.mute() : player.unmute()), [player, muted]);
    useLayoutEffect(() => player.setLoop(loop), [player, loop]);
    useLayoutEffect(() => player.setTitle(title), [player, title]);
    useLayoutEffect(() => {
        playing ? player.play() : player.pause();
    }, [player, playing]);
    useLayoutEffect(() => {
        player.setPoster(coverUrl || '');
    }, [coverUrl, player]);
    return null;
};
export function registerPlayableModule(name, module) {
    registerModule(name, module);
}
export const Playable = ({ playableChildren, ...props }) => {
    const { rtl } = props;
    const [player, setPlayer] = useState();
    const setRef = useCallback((div) => {
        if (div) {
            const instance = create({
                fillAllSpace: true,
                rtl,
            });
            instance.attachToElement(div);
            setPlayer(instance);
        }
    }, [rtl]);
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { ref: setRef, className: s.playable }, player && playableChildren
            ? createPortal(playableChildren(player), player.getElement())
            : null),
        player ? React.createElement(Player, { ...props, player: player }) : null));
};
//# sourceMappingURL=Playable.js.map